import React, {
  createContext,
  useCallback,
  useContext,
  useEffect
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';

const BRAND = 'ShipStation Returns';
const API_KEY = process.env.REACT_APP_SEGMENT_API_KEY;
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';

const analytics = new AnalyticsBrowser();

/**
 * @type {import('react').Context<{
 *   isTrackable: boolean;
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 * }>}
 */
export const SegmentContext = createContext();

export function SegmentProvider({ children = null }) {
  const { userData, storeProperties } = useContext(AuthContext);

  const storeName = _get(storeProperties, 'store_name', '');
  const ssSellerId = _get(storeProperties, 'ss_seller_id', '');
  const storeUniqueName = _get(storeProperties, 'store_unique_name', '');
  const isSSR = _get(storeProperties, 'is_ssr_retailer', false);
  const merchantId = !!storeUniqueName ? `SSR-${storeUniqueName}` : null;

  const isTrackable = API_KEY && (!IS_PROD || isSSR);

  useEffect(() => {
    const userId = _get(userData, 'userId');
    const email = _get(userData, 'email');
    const firstName = _get(userData, 'firstName');
    const lastName = _get(userData, 'lastName');

    if (isTrackable && userId && merchantId && storeName) {
      try {
        analytics.load({ writeKey: API_KEY });
        analytics.identify(userId, {
          group_id: merchantId,
          cx_merchant_id: merchantId,
          customer_id: userId,
          user_id: userId,
          brand: BRAND,
          email,
          first_name: firstName,
          last_name: lastName,
          company_name: storeName,
          seller_id: ssSellerId,
          user_role: 'SHOPPER'
        });
      } catch (error) {
        console.error('Failed to identify user in Segment:', error);
      }

      return () => {
        try {
          analytics.reset();
        } catch (error) {
          console.error('Failed to reset Segment:', error);
        }
      };
    }
  }, [isTrackable, userData, merchantId, storeName, ssSellerId]);

  const track = useCallback(
    (eventName, eventProperties = {}) => {
      const userId = _get(userData, 'userId', '');
      const email = _get(userData, 'email', '');

      if (!isTrackable || !userId || !merchantId) return;

      try {
        analytics.track(eventName, {
          brand: BRAND,
          cx_merchant_id: merchantId,
          user_id: userId,
          email,
          ...eventProperties
        });
      } catch (error) {
        console.error('Failed to track event in Segment:', error);
      }
    },
    [isTrackable, userData, merchantId]
  );

  return (
    <SegmentContext.Provider value={{ isTrackable, track }}>
      {children}
    </SegmentContext.Provider>
  );
}
